import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { ImportDialog } from '../features/ImportDialog';
import type { Enterprise, EnterpriseDetail, Page } from '../lib/types';
import { HEALTH_LABEL, HEALTH_TONE, IMPORTANCE_TONE, titleCase } from '../lib/format';
import {
  Badge,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorNotice,
  Loading,
  Button,
  Dialog,
  Field,
  Input,
  PageHeader,
  Textarea,
  useToast,
  SectionNote,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

/**
 * §6.1 Enterprises.
 *
 * An enterprise is the parent of several independently-run accounts — the J&J and
 * Novo Nordisk shape from §5. `known_domains` matters more than it looks: it is
 * what lets an email from an unrecognised address still be matched to the right
 * customer (§15, step 2).
 */
export function EnterprisesPage() {
  const { me } = useSession();
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const query = useQuery({
    queryKey: ['enterprises'],
    queryFn: () => api.get<Page<Enterprise>>('/enterprises', { limit: 100 }),
  });

  const enterprises = query.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Enterprises"
        description="Customer groups. Each account beneath an enterprise has its own owner, cadence and SLA."
        actions={
          me.capabilities.canCreateEnterprises && (
            <>
              <Button size="sm" variant="secondary" onClick={() => setImporting(true)}>
                Import
              </Button>
              <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                New enterprise
              </Button>
            </>
          )
        }
      />
      {importing && (
        <ImportDialog
          entity="enterprises"
          label="enterprises"
          invalidate={[['enterprises']]}
          onClose={() => setImporting(false)}
        />
      )}

      {creating && <CreateEnterpriseDialog onClose={() => setCreating(false)} />}

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}
      {query.data && enterprises.length === 0 && (
        <EmptyState
          title="No enterprises visible"
          description="You see an enterprise when you have access to at least one account beneath it."
        />
      )}

      {enterprises.length > 0 && (
        <Card padded={false}>
          <Table>
            <thead>
              <tr>
                <Th>Enterprise</Th>
                <Th>Code</Th>
                <Th>Legal name</Th>
                <Th>Primary domain</Th>
                <Th>Known domains</Th>
                <Th>Status</Th>
                <Th align="right">Accounts</Th>
              </tr>
            </thead>
            <tbody>
              {enterprises.map((enterprise) => (
                <Tr key={enterprise.id}>
                  <Td>
                    <Link
                      to={`/enterprises/${enterprise.id}`}
                      className="font-medium text-accent hover:underline"
                    >
                      {enterprise.name}
                    </Link>
                  </Td>
                  <Td className="font-mono text-xs text-ink-2">{enterprise.code}</Td>
                  <Td className="text-ink-2">{enterprise.legalName ?? '—'}</Td>
                  <Td className="font-mono text-xs text-ink-2">
                    {enterprise.primaryDomain ?? '—'}
                  </Td>
                  <Td>
                    <span className="flex flex-wrap gap-1">
                      {enterprise.knownDomains.map((domain) => (
                        <Badge key={domain} tone="neutral" dot={false}>
                          {domain}
                        </Badge>
                      ))}
                    </span>
                  </Td>
                  <Td className="text-ink-2">{titleCase(enterprise.status)}</Td>
                  <Td align="right" className="tnum">
                    {enterprise.accountCount ?? '—'}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <div className="mt-3">
        <SectionNote>
          Known domains are used by deterministic account matching: an email from an unknown person
          at a known domain is still attributed to the right enterprise before any AI is involved.
        </SectionNote>
      </div>
    </>
  );
}

export function EnterpriseDetailPage() {
  const { enterpriseId = '' } = useParams();

  const query = useQuery({
    queryKey: ['enterprise', enterpriseId],
    queryFn: () => api.get<EnterpriseDetail>(`/enterprises/${enterpriseId}`),
  });

  const enterprise = query.data;

  return (
    <>
      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {enterprise && (
        <>
          <PageHeader
            title={enterprise.name}
            description={enterprise.legalName ?? undefined}
            meta={
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-border bg-surface px-4 py-3 sm:grid-cols-5">
                <Detail label="Code">
                  <span className="font-mono">{enterprise.code}</span>
                </Detail>
                <Detail label="Status">{titleCase(enterprise.status)}</Detail>
                <Detail label="Primary domain">{enterprise.primaryDomain ?? '—'}</Detail>
                <Detail label="Known domains">
                  <span className="flex flex-wrap gap-1">
                    {enterprise.knownDomains.length === 0
                      ? '—'
                      : enterprise.knownDomains.map((domain) => (
                          <Badge key={domain} tone="neutral" dot={false}>
                            {domain}
                          </Badge>
                        ))}
                  </span>
                </Detail>
                <Detail label="Accounts you can see">{enterprise.accounts.length}</Detail>
              </div>
            }
          />

          {enterprise.notes && (
            <div className="mb-4">
              <Card>
                <CardHeader title="Notes" />
                <p className="text-sm whitespace-pre-wrap text-ink">{enterprise.notes}</p>
              </Card>
            </div>
          )}

          <Card padded={false}>
            <div className="px-4 pt-4">
              <CardHeader
                title="Accounts"
                count={enterprise.accounts.length}
                description="Each one is independently owned and independently measured."
              />
            </div>
            <Table>
              <thead>
                <tr>
                  <Th>Account</Th>
                  <Th>Code</Th>
                  <Th>Importance</Th>
                  <Th>Health</Th>
                  <Th>Status</Th>
                  <Th>Lifecycle</Th>
                  <Th>Primary CSM</Th>
                </tr>
              </thead>
              <tbody>
                {enterprise.accounts.map((account) => (
                  <Tr key={account.id}>
                    <Td>
                      <Link
                        to={`/accounts/${account.id}`}
                        className="font-medium text-accent hover:underline"
                      >
                        {account.parentAccountId && <span className="text-muted">↳ </span>}
                        {account.name}
                      </Link>
                    </Td>
                    <Td className="font-mono text-xs text-ink-2">{account.accountCode ?? '—'}</Td>
                    <Td>
                      <Badge
                        tone={IMPORTANCE_TONE[account.importance]}
                        dot={account.importance === 'strategic'}
                      >
                        {titleCase(account.importance)}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge tone={HEALTH_TONE[account.health]}>
                        {HEALTH_LABEL[account.health]}
                      </Badge>
                    </Td>
                    <Td className="text-ink-2">{titleCase(account.status)}</Td>
                    <Td className="text-ink-2">{titleCase(account.lifecycle)}</Td>
                    <Td className="text-xs text-ink-2">{account.primaryCsmName}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
}


/**
 * §6.1 — an enterprise is the customer group; accounts hang beneath it.
 *
 * Known domains matter more than they look: §15 step 2 matches an inbound email
 * to an account by its sender domain, so an enterprise created without them
 * will never match anything automatically once ingest is live.
 */
/** Mirrors `enterprises_code_ck` — validated here only to keep the button honest. */
const ENTERPRISE_CODE_RE = /^[A-Z0-9]([A-Z0-9-]*[A-Z0-9])?$/;

function CreateEnterpriseDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [knownDomains, setKnownDomains] = useState('');
  const [notes, setNotes] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.post('/enterprises', {
        code,
        name,
        legalName: legalName || null,
        primaryDomain: primaryDomain || null,
        knownDomains: knownDomains
          .split(/[\s,]+/)
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean),
        notes: notes || null,
      }),
    onSuccess: () => {
      toast('Enterprise created');
      void queryClient.invalidateQueries({ queryKey: ['enterprises'] });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="New enterprise"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={create.isPending || !name.trim() || !ENTERPRISE_CODE_RE.test(code)}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Saving…' : 'Create enterprise'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {create.error && <ErrorNotice error={create.error} />}
        <div className="grid grid-cols-3 gap-3">
          <Field
            label="Code"
            required
            hint="Short, permanent, and how imports refer to this enterprise."
          >
            <Input
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="JNJ-MT"
              className="font-mono"
            />
          </Field>
          <Field label="Name" required>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Johnson &amp; Johnson"
            />
          </Field>
          <Field label="Legal name">
            <Input value={legalName} onChange={(event) => setLegalName(event.target.value)} />
          </Field>
        </div>
        <Field label="Primary domain" hint="Used to match inbound mail to this customer.">
          <Input
            value={primaryDomain}
            onChange={(event) => setPrimaryDomain(event.target.value)}
            placeholder="its.jnj.com"
          />
        </Field>
        <Field label="Other known domains" hint="Comma or space separated.">
          <Input
            value={knownDomains}
            onChange={(event) => setKnownDomains(event.target.value)}
            placeholder="jnj.com, janssen.com"
          />
        </Field>
        <Field label="Notes">
          <Textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
